// src/services/apiService.js - FIXED VERSION
const API_BASE = 'https://kola-lead-capture-server.vercel.app/api';

const apiCall = async (endpoint, options = {}) => {
  const token = localStorage.getItem('adminToken');
  
  const config = {
    headers: {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
    },
    ...options
  };

  // Add body handling for non-GET requests
  if (options.body && typeof options.body === 'object') {
    config.body = JSON.stringify(options.body);
  }

  try {
    console.log(`🔄 API CALL: ${API_BASE}${endpoint}`);
    const response = await fetch(`${API_BASE}${endpoint}`, config);
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error(`❌ API ERROR ${response.status}:`, errorText);
      throw new Error(`HTTP ${response.status}: ${errorText}`);
    }
    
    const data = await response.json();
    console.log(`✅ API SUCCESS: ${endpoint}`, data);
    return data;
  } catch (error) {
    console.error(`❌ API FAILED: ${endpoint}`, error);
    throw error;
  }
};

// CORE API FUNCTIONS
export const testAPI = async () => {
  return apiCall('/test');
};

export const adminLogin = async (credentials) => {
  return apiCall('/auth/login', {
    method: 'POST',
    body: credentials
  });
};

export const submitForm = async (formData) => {
  return apiCall('/forms/submit', {
    method: 'POST',
    body: formData
  });
};

// Analytics functions
export const getAnalytics = async (timeRange = '7days') => {
  return apiCall(`/analytics/analytics?range=${timeRange}`);
};

export const getStats = async (timeRange = '7days') => {
  try {
    return await apiCall(`/analytics/stats?range=${timeRange}`);
  } catch (error) {
    console.warn('Stats endpoint not available, returning defaults');
    return {
      totalLeads: 0,
      leadsToday: 0,
      conversionRate: 0,
      averageQuote: 0,
      highPriorityLeads: 0,
      totalRevenue: 0,
      wonDeals: 0,
      responseRate: 0
    };
  }
};

// FIXED: Use the apiCall helper for updateSubmission
export const updateSubmission = async (id, submissionData) => {
  return apiCall(`/forms/submissions/${id}`, {
    method: 'PATCH',
    body: submissionData
  });
};

export const getSubmissions = async () => {
  try {
    const result = await apiCall('/forms/submissions');
    console.log('📦 Raw submissions result:', result);
    
    // Handle different response formats
    let submissionsArray = [];
    
    if (Array.isArray(result)) {
      submissionsArray = result;
    } else if (result && Array.isArray(result.submissions)) {
      submissionsArray = result.submissions;
    } else if (result && result.data) {
      submissionsArray = Array.isArray(result.data) ? result.data : [result.data];
    } else {
      console.warn('❌ Unexpected submissions format, returning empty array');
      return [];
    }
    
    // Convert snake_case to camelCase for frontend compatibility
    const convertedSubmissions = submissionsArray.map(submission => {
      // Create a fallback date if created_at is null
      const createdAt = submission.created_at || submission.date || new Date().toISOString();
      const updatedAt = submission.updated_at || createdAt;
      
      return {
        // Core fields conversion
        id: submission.id,
        customerName: submission.customer_name,
        email: submission.email,
        phoneNumber: submission.phone_number,
        postcode: submission.postcode,
        address: submission.address,
        county: submission.county,
        location: submission.location,
        propertyType: submission.property_type,
        approxArea: submission.approx_area,
        condition: submission.condition,
        preferredStart: submission.preferred_start,
        quoteAmount: submission.quote_amount,
        priority: submission.priority,
        additionalNotes: submission.additional_notes,
        
        // Array fields
        preferredContact: submission.preferred_contact || [],
        existingSurface: submission.existing_surface || [],
        serviceRequired: submission.service_required || [],
        
        // Status and metadata
        status: submission.status || 'new',
        leadOrigin: submission.lead_origin || 'web-form',
        generatedBy: submission.generated_by || 'user',
        
        // Date handling - ensure we always have valid dates
        date: createdAt,
        createdAt: createdAt,
        updatedAt: updatedAt
      };
    });
    
    console.log('🔄 Converted submissions with dates:', convertedSubmissions.map(sub => ({
      id: sub.id,
      customerName: sub.customerName,
      createdAt: sub.createdAt,
      date: sub.date
    })));
    
    return convertedSubmissions;
    
  } catch (error) {
    console.error('❌ getSubmissions failed:', error);
    throw error;
  }
};

export const updateSubmissionStatus = async (submissionId, status) => {
  return apiCall(`/forms/submissions/${submissionId}`, {
    method: 'PATCH',
    body: { status }
  });
};

export const deleteSubmission = async (submissionId) => {
  return apiCall(`/forms/submissions/${submissionId}`, {
    method: 'DELETE'
  });
};

export const getSubmission = async (submissionId) => {
  return apiCall(`/forms/submissions/${submissionId}`);
};

export const createSubmission = async (formData) => {
  return apiCall('/forms/submissions', {
    method: 'POST',
    body: formData
  });
};

// Debug and utility functions
export const debugAllEndpoints = async () => {
  const results = {};
  const token = localStorage.getItem('adminToken');

  try {
    // Test basic API
    results.test = await testAPI();
    
    // Test form submission
    try {
      results.formSubmit = await submitForm({
        customer_name: "Test Customer Debug",
        email: "debug@test.com",
        phone_number: "07123456789",
        service_required: ["resin driveway"],
        location: "Test City",
        property_type: "residential"
      });
    } catch (error) {
      results.formSubmit = { error: error.message };
    }

    // Test protected endpoints
    if (token) {
      try {
        results.submissions = await getSubmissions();
      } catch (error) {
        results.submissions = { error: error.message };
      }

      try {
        results.analytics = await getAnalytics();
      } catch (error) {
        results.analytics = { error: error.message };
      }

      try {
        results.stats = await getStats();
      } catch (error) {
        results.stats = { error: error.message };
      }
    } else {
      results.auth = "Not authenticated";
    }

  } catch (error) {
    results.overallError = error.message;
  }

  console.log('🔧 DEBUG RESULTS:', results);
  return results;
};

export const healthCheck = async () => {
  return apiCall('/health');
};

export const testDatabase = async () => {
  return apiCall('/debug/db-test');
};